import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Send, Loader2, Clock3, BadgeCheck, FileText } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { Input } from "@/components/ui/input";

const FLEET_SIZES = [
  "Owner-operator (1 truck)",
  "Small fleet (2-10 trucks)",
  "Mid-size fleet (11-50 trucks)",
  "Large fleet (50+ trucks)",
];

const initialForm = () => ({
  company_name: "",
  contact_name: "",
  email: "",
  phone: "",
  fleet_size: FLEET_SIZES[0],
  trucks: "",
  last_invoice: window.__prefillInvoice ? String(window.__prefillInvoice) : "",
});

const PERKS = [
  { icon: Clock3, text: "Callback with a real number within one business day" },
  { icon: BadgeCheck, text: "No obligation, no credit checks" },
  { icon: FileText, text: "Your last invoice unlocks the up-to-50% discount" },
];

export const QuoteForm = () => {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const prefill = () =>
      setForm((f) => ({ ...f, last_invoice: String(window.__prefillInvoice || f.last_invoice) }));
    window.addEventListener("fleetguard:prefill-invoice", prefill);
    return () => window.removeEventListener("fleetguard:prefill-invoice", prefill);
  }, []);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/quotes", {
        ...form,
        phone: form.phone || undefined,
        trucks: Number(form.trucks),
        last_invoice: Number(form.last_invoice),
      });
      if (typeof window.gtag === "function") {
        window.gtag("event", "conversion", {
          send_to: "AW-18440099660/DreRCM7iu5QdEMyu9thE",
          value: 1.0,
          currency: "USD",
        });
      }
      toast.success("Request received! Your agreement download has started.");
      setForm(initialForm());
      const a = document.createElement("a");
      a.href = "/api/agreement/download";
      a.setAttribute("download", "");
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="quote" data-testid="quote-form-section" className="bg-white py-16 sm:py-24">
      <div className="mx-auto grid max-w-7xl items-start gap-14 px-4 sm:px-6 lg:grid-cols-2 lg:gap-20 lg:px-8">
        <div>
          <Reveal>
            <p className="eyebrow">Get a Quote</p>
            <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-brand-navy sm:text-4xl lg:text-[2.75rem] lg:leading-[1.1]">
              Tell us about your fleet. We'll do the math.
            </h2>
            <p className="mt-6 text-lg leading-relaxed text-slate-600">
              Share a few details and a licensed agent will call you back with a real, binding
              number — including your first-term discount of up to 50% calculated from the invoice
              you're paying today.
            </p>
          </Reveal>
          <Reveal delay={0.12}>
            <ul className="mt-8 space-y-4">
              {PERKS.map((p) => (
                <li key={p.text} className="flex items-center gap-3 text-base font-medium text-brand-navy">
                  <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-brand-ice text-brand-blue">
                    <p.icon className="h-4 w-4" />
                  </span>
                  {p.text}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <form
            onSubmit={submit}
            data-testid="quote-form"
            className="rounded-3xl border border-slate-200 bg-white p-7 shadow-2xl shadow-slate-900/10 sm:p-9"
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label htmlFor="quote-company" className="mb-1.5 block text-sm font-semibold text-brand-navy">
                  Company name
                </label>
                <Input
                  id="quote-company"
                  required
                  value={form.company_name}
                  onChange={set("company_name")}
                  placeholder="Delgado Hauling LLC"
                  data-testid="quote-input-company"
                />
              </div>
              <div>
                <label htmlFor="quote-name" className="mb-1.5 block text-sm font-semibold text-brand-navy">
                  Your name
                </label>
                <Input
                  id="quote-name"
                  required
                  value={form.contact_name}
                  onChange={set("contact_name")}
                  placeholder="Marcus Delgado"
                  data-testid="quote-input-name"
                />
              </div>
              <div>
                <label htmlFor="quote-email" className="mb-1.5 block text-sm font-semibold text-brand-navy">
                  Email
                </label>
                <Input
                  id="quote-email"
                  type="email"
                  required
                  value={form.email}
                  onChange={set("email")}
                  placeholder="dispatch@yourfleet.com"
                  data-testid="quote-input-email"
                />
              </div>
              <div>
                <label htmlFor="quote-phone" className="mb-1.5 block text-sm font-semibold text-brand-navy">
                  Phone <span className="font-normal text-slate-400">(optional)</span>
                </label>
                <Input
                  id="quote-phone"
                  type="tel"
                  value={form.phone}
                  onChange={set("phone")}
                  placeholder="(555) 210-4400"
                  data-testid="quote-input-phone"
                />
              </div>
              <div>
                <label htmlFor="quote-fleet" className="mb-1.5 block text-sm font-semibold text-brand-navy">
                  Fleet size
                </label>
                <select
                  id="quote-fleet"
                  value={form.fleet_size}
                  onChange={set("fleet_size")}
                  data-testid="quote-select-fleet"
                  className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-brand-navy outline-none transition-colors focus:border-brand-blue focus:ring-2 focus:ring-blue-200"
                >
                  {FLEET_SIZES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="quote-trucks" className="mb-1.5 block text-sm font-semibold text-brand-navy">
                  Number of trucks
                </label>
                <Input
                  id="quote-trucks"
                  type="number"
                  min="1"
                  required
                  value={form.trucks}
                  onChange={set("trucks")}
                  placeholder="6"
                  data-testid="quote-input-trucks"
                />
              </div>
              <div>
                <label htmlFor="quote-invoice" className="mb-1.5 block text-sm font-semibold text-brand-navy">
                  Last monthly insurance invoice ($)
                </label>
                <Input
                  id="quote-invoice"
                  type="number"
                  min="0"
                  required
                  value={form.last_invoice}
                  onChange={set("last_invoice")}
                  placeholder="1450"
                  data-testid="quote-input-invoice"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={submitting}
              data-testid="quote-submit"
              className="mt-7 flex w-full items-center justify-center gap-2 rounded-full bg-brand-blue px-6 py-4 text-base font-semibold text-white shadow-lg shadow-blue-600/25 transition-all hover:-translate-y-0.5 hover:bg-brand-bluedeep disabled:opacity-60"
            >
              {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-4 w-4" />}
              Request my quote
            </button>
            <p className="mt-4 text-center text-xs leading-relaxed text-slate-400">
              By submitting you agree to be contacted about insurance options. We never sell your data.
            </p>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
